import { Capacitor } from "@capacitor/core";
import { Box, CircularProgress, Stack } from "@mui/material";
import { showErrorToast } from "@src/appV2/lib/Notifications";
import { Text } from "@src/appV2/redesign/components/Text";
import { Title } from "@src/appV2/redesign/components/Title";
// eslint-disable-next-line no-restricted-imports -- Bridge to old app code
import { signInWithEmailLink } from "@src/containers/enter/firebase";
// eslint-disable-next-line no-restricted-imports -- Need Firebase to check auth state
import { FirebaseAuth } from "@src/utils/firebase";
import queryString from "query-string";
import { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { waitForSession } from "../utils/waitForSession";

/**
 * Mobile email verification component.
 *
 * Flow:
 * 1. User clicks email link → Opens in Safari with /mobile/login/verify
 * 2. This component loads in browser, completes Firebase auth
 * 3. After success, redirects to custom scheme to open native app
 */
export function MobileVerify() {
  const location = useLocation();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  const handleBackToLogin = useCallback(() => {
    history.push("/mobile/login");
  }, [history]);

  // Handle auth completion in native app when coming from browser
  const handleNativeAppAuth = useCallback(
    async (qs: queryString.ParsedQuery) => {
      // Decode the auth URL from the browser (contains Firebase params)
      const authUrl = decodeURIComponent(qs.authUrl as string);
      const authQs = queryString.parse(new URL(authUrl).search);
      const emailToVerify = (authQs.email as string) || localStorage.getItem("emailToVerify");

      if (!emailToVerify) {
        setError("Email address not found. Please try logging in again.");
        setLoading(false);
        return;
      }

      // Store the auth URL in sessionStorage for signInWithEmailLink to use
      sessionStorage.setItem("originalAuthURL", authUrl);

      // Complete Firebase authentication in the native app
      const result = await signInWithEmailLink(emailToVerify);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Legacy error handling
      if ((result as any).error) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Legacy error handling
        const errorMessage = (result as any).error as string;
        throw new Error(errorMessage);
      }

      // Success! Navigate to the requested URL
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- Need || to handle empty strings
      const redirectUrl = (qs.requestedUrl as string | undefined) || "/mobile/calendar";

      // Clean up verification data to prevent reuse
      localStorage.removeItem("emailToVerify");
      sessionStorage.removeItem("originalAuthURL");

      // Wait for Redux session to initialize before navigating
      // The onIdTokenChanged listener should fire after signInWithEmailLink completes
      // and will populate userId via async API calls

      await waitForSession();

      history.replace(redirectUrl);
    },
    [history]
  );

  useEffect(() => {
    async function verifyEmail() {
      try {
        // Check if user is already authenticated - if so, just redirect
        const { currentUser } = FirebaseAuth;
        if (currentUser) {
          const qs = queryString.parse(location.search);
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- Need || to handle empty strings
          const redirectUrl = (qs.requestedUrl as string | undefined) || "/mobile/calendar";

          // Wait for Redux session to initialize before navigating
          await waitForSession();

          history.push(redirectUrl);
          return;
        }

        const qs = queryString.parse(location.search);

        // Guard: Only proceed if we have Firebase auth params or authUrl
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- Need || to check for truthy values
        const hasAuthParams = qs.authUrl || qs.apiKey || qs.oobCode;
        if (!hasAuthParams) {
          setError("No verification link found. Please request a new login link.");
          setLoading(false);
          return;
        }

        // Check if we're coming from the browser with authUrl parameter
        if (qs.authUrl && Capacitor.isNativePlatform()) {
          await handleNativeAppAuth(qs);
          return;
        }

        // Check if we're in a mobile browser (not Capacitor app)
        // If so, DON'T complete auth here - just pass the URL to the native app
        if (!Capacitor.isNativePlatform()) {
          // Get redirect URL from query params
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- Need || to handle empty strings
          const redirectUrl = (qs.requestedUrl as string | undefined) || "/mobile/calendar";

          // Pass the current URL (with Firebase auth params) to the app WITHOUT consuming the link
          const currentUrl = window.location.href;
          const encodedUrl = encodeURIComponent(currentUrl);
          const deepLink = `clipboardworks.workplace:///mobile/login/verify?authUrl=${encodedUrl}&requestedUrl=${encodeURIComponent(
            redirectUrl
          )}`;

          // Open native app with deep link containing auth URL
          window.location.href = deepLink;
          return;
        }

        // If we're already in Capacitor app (shouldn't happen normally), complete auth here
        const emailToVerify = (qs.email as string) || localStorage.getItem("emailToVerify");

        if (!emailToVerify) {
          setError("Email address not found. Please try logging in again.");
          setLoading(false);
          return;
        }

        // Complete Firebase authentication
        const result = await signInWithEmailLink(emailToVerify);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Legacy error handling
        if ((result as any).error) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Legacy error handling
          const errorMessage = (result as any).error as string;
          throw new Error(errorMessage);
        }

        // Success! Navigate to the requested URL
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- Need || to handle empty strings
        const redirectUrl = (qs.requestedUrl as string | undefined) || "/mobile/calendar";

        // Clean up verification data to prevent reuse
        localStorage.removeItem("emailToVerify");
        sessionStorage.removeItem("originalAuthURL");

        // Wait for Redux session to initialize before navigating
        await waitForSession();

        history.replace(redirectUrl);
      } catch (error_: unknown) {
        const message = error_ instanceof Error ? error_.message : "Verification failed";
        setError(message);
        showErrorToast(message);
        setLoading(false);
      }
    }

    void verifyEmail();
  }, [location.search, history, handleNativeAppAuth]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 3,
        backgroundColor: "background.default",
      }}
    >
      <Stack spacing={3} alignItems="center" sx={{ maxWidth: 400 }}>
        {loading && (
          <>
            <CircularProgress size={48} />
            <Title component="h5" variant="h5">
              Verifying your email...
            </Title>
            <Text>Please wait while we sign you in.</Text>
          </>
        )}
        {!loading && error && (
          <>
            <Title component="h5" variant="h5">
              Verification Failed
            </Title>
            <Text>{error}</Text>
            <Text
              variant="body2"
              sx={{ cursor: "pointer", textDecoration: "underline" }}
              onClick={handleBackToLogin}
            >
              Back to Login
            </Text>
          </>
        )}
        {!loading && !error && (
          <>
            <Title component="h5" variant="h5">
              Success!
            </Title>
            <Text>Opening the app...</Text>
          </>
        )}
      </Stack>
    </Box>
  );
}
